import React, { useMemo, useState } from "react";
import { UserSettingLayoutContainer } from "../UserSettingLayoutContainer";
import { useTranslate } from "@obsbot/remo-i18n";
import { OverlayDisplaySetting, useGlobalStore, usePreferencesStore } from "@obsbot/remo-stores";
import { SettingSelector, SettingSlider, SettingSwitch } from "remo-components";
import { RouteNameType, usePushHistory } from "../../utils/routes";
import "./index.less";

const GameoverlaySettingPage = (): React.ReactElement => {
    const t = useTranslate();
    const globalStore = useGlobalStore();
    const preferenceStore = usePreferencesStore(globalStore.userInfo?.user_id ?? "")?.(
        state => state,
    );
    const pushHistory = usePushHistory();
    // 是否全屏显示游戏覆盖
    const [fullscreenOverlay, setFullscreenOverlay] = useState(
        preferenceStore?.fullscreenShowGameoverlay ?? false,
    );
    // 是否桌面显示游戏覆盖
    const [desktopOverlay, setDesktopOverlay] = useState(
        preferenceStore?.desktopShowGameoverlay ?? false,
    );
    // 覆盖工具-显示设置的数组
    const overlayDisplayOptions = useMemo(() => {
        return Object.values(OverlayDisplaySetting).map(option => ({
            label: t("settings.gameoverlay-setting." + option),
            value: option,
            title: "",
        }));
    }, [t]);
    // 覆盖工具-显示设置的值
    const [overlayDisplaySetting, setOverlayDisplaySetting] = useState(
        preferenceStore?.overlayDisplaySetting ?? OverlayDisplaySetting.Always,
    );
    // 显示的最大用户数
    const [maxShowUser, setMaxShowUser] = useState(preferenceStore?.maxShowUser ?? 5);
    // 处理全屏显示游戏覆盖的切换
    const handleFullscreenOverlayChange = (isOverlay: boolean): void => {
        setFullscreenOverlay(isOverlay);
        preferenceStore?.updateFullscreenOverlay(isOverlay);
    };
    // 处理桌面显示游戏覆盖的切换
    const handleDesktopOverlayChange = (isOverlay: boolean): void => {
        setDesktopOverlay(isOverlay);
        preferenceStore?.updateDesktopOverlay(isOverlay);
    };
    // 处理显示设置的切换
    const handleOverlayDisplaySettingChange = (val: OverlayDisplaySetting): void => {
        setOverlayDisplaySetting(val);
        preferenceStore?.updateOverlayDisplaySetting(val);
    };
    // 处理显示的最大用户数改变
    const handleMaxShowUserChange = (val: number): void => {
        setMaxShowUser(val);
        preferenceStore?.updateMaxShowUser(val);
    };
    // 处理修改游戏覆盖样式的点击事件
    const handleClickEditOverlay = (): void => {
        pushHistory(RouteNameType.GameOverlaySettingViewPage);
    };
    return (
        <UserSettingLayoutContainer>
            <div className="gameoverlay-setting-container">
                <div className="gameoverlay-common-setting">
                    <div className="setting-page-title">
                        {t("settings.gameoverlay-setting.commonSetting")}
                    </div>
                    <div className="setting-page-content">
                        <div className="setting-show-item-between">
                            <div className="setting-page-label">
                                <span>{t("settings.gameoverlay-setting.fullscreenOverlay")}</span>
                                <span className="tips">
                                    {t("settings.gameoverlay-setting.fullscreenOverlayTip")}
                                </span>
                            </div>
                            <div className="rtc-setting-page-input">
                                <SettingSwitch
                                    checked={fullscreenOverlay}
                                    onChange={handleFullscreenOverlayChange}
                                />
                            </div>
                        </div>
                        <div className="setting-show-item-between">
                            <div className="setting-page-label">
                                <span>{t("settings.gameoverlay-setting.desktopOverlay")}</span>
                                <span className="tips">
                                    {t("settings.gameoverlay-setting.desktopOverlayTip")}
                                </span>
                            </div>
                            <div className="rtc-setting-page-input">
                                <SettingSwitch
                                    checked={desktopOverlay}
                                    onChange={handleDesktopOverlayChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="gameoverlay-overlay-setting">
                    <div className="setting-page-title">
                        {t("settings.gameoverlay-setting.overlayTool")}
                    </div>
                    <div className="setting-page-content">
                        <div className="setting-show-item-between">
                            <div className="setting-page-label">
                                {t("settings.gameoverlay-setting.styleSetting")}
                            </div>
                            <div className="setting-page-input">
                                <div className="setting-edit" onClick={handleClickEditOverlay}>
                                    {t("settings.account-setting.edit")}
                                </div>
                            </div>
                        </div>
                        <div className="setting-show-item-between">
                            <div className="setting-page-label">
                                {t("settings.gameoverlay-setting.displaySetting")}
                            </div>
                            <div className="setting-page-input">
                                <SettingSelector
                                    key={overlayDisplaySetting}
                                    className="setting-gameoverlay-selector"
                                    defaultValue={overlayDisplaySetting}
                                    options={overlayDisplayOptions}
                                    onChange={handleOverlayDisplaySettingChange}
                                />
                            </div>
                        </div>
                        <div className="setting-show-item-between">
                            <div className="setting-page-label" style={{ width: "50%" }}>
                                {t("settings.gameoverlay-setting.maxShowUser")}
                            </div>
                            <div className="setting-page-input slider">
                                <SettingSlider
                                    bg_handle="transparent"
                                    bg_track="var(--main-violet-1)"
                                    marks={{ 0: "0", 5: "5", 10: "10" }}
                                    max={10}
                                    min={0}
                                    rootClassName="switch-line"
                                    step={1}
                                    value={maxShowUser}
                                    onChange={handleMaxShowUserChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </UserSettingLayoutContainer>
    );
};

export default GameoverlaySettingPage;
